import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppVersionTelemetry } from '../../features/telemetry/useAppVersionTelemetry';
import { Sparkles, RefreshCw, X } from 'lucide-react';

const AUTO_RELOAD_DELAY_SECONDS = 8;

export const PwaUpdateWatcher: React.FC = () => {
  const telemetry = useAppVersionTelemetry();
  const { isStale, remoteManifest, purgeAndReload } = telemetry;

  const [dismissed, setDismissed] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_RELOAD_DELAY_SECONDS);
  const [isUpdating, setIsUpdating] = useState(false);
  const timerRef = useRef<any>(null);
  // Proteção contra duplo disparo em React StrictMode e estado concorrente
  const hasTriggeredRef = useRef(false);

  // Limpa o parâmetro _fresh da URL após o reload
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search.includes('_fresh=')) {
      const cleanUrl = window.location.pathname + window.location.search.replace(/[?&]_fresh=\d+/, '').replace(/^&/, '?');
      window.history.replaceState({}, document.title, cleanUrl || '/');
    }
  }, []);

  const handleApplyUpdate = useCallback(() => {
    if (hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;
    setIsUpdating(true);
    if (timerRef.current) clearInterval(timerRef.current);
    void purgeAndReload();
  }, [purgeAndReload]);

  // Inicia contagem regressiva quando isStale vira true
  useEffect(() => {
    if (!isStale || dismissed) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    // Reseta a flag ao iniciar nova contagem (ex: usuário fez dismiss e depois isStale voltou)
    hasTriggeredRef.current = false;
    setCountdown(AUTO_RELOAD_DELAY_SECONDS);

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          // Sai do ciclo de setState antes de chamar o reload
          setTimeout(() => handleApplyUpdate(), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isStale, dismissed, handleApplyUpdate]);

  if (!isStale || dismissed) {
    return null;
  }

  const progressPercent = ((AUTO_RELOAD_DELAY_SECONDS - countdown) / AUTO_RELOAD_DELAY_SECONDS) * 100;

  return (
    <div
      style={{
        position: 'fixed',
        top: 'max(12px, env(safe-area-inset-top, 12px))',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 99999,
        width: 'calc(100% - 24px)',
        maxWidth: 460,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
        border: '1.5px solid rgba(129, 140, 248, 0.4)',
        borderRadius: 16,
        padding: '14px 16px',
        color: '#ffffff',
        boxShadow: '0 12px 36px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(12px)',
        animation: 'pwa-slide-down 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {/* Barra de progresso do timer */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: 'rgba(255, 255, 255, 0.1)',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${progressPercent}%`,
            background: 'linear-gradient(90deg, #6366f1, #38bdf8)',
            transition: 'width 1s linear',
          }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: 'rgba(99, 102, 241, 0.2)',
              border: '1px solid rgba(99, 102, 241, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Sparkles size={20} style={{ color: '#818cf8', animation: 'pwa-pulse 2s infinite' }} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 800, color: '#ffffff' }}>
                Nova Atualização Disponível!
              </span>
              <span
                style={{
                  background: '#2563eb',
                  color: '#ffffff',
                  fontSize: '0.66rem',
                  fontWeight: 800,
                  padding: '2px 6px',
                  borderRadius: 6,
                  fontFamily: 'monospace',
                }}
              >
                {remoteManifest?.version || 'Nova'}
              </span>
            </div>
            <p style={{ fontSize: '0.74rem', color: '#cbd5e1', margin: '3px 0 0', lineHeight: 1.35 }}>
              Atualizando automaticamente em <strong>{countdown}s</strong> para aplicar melhorias...
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setDismissed(true)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.4)',
            cursor: 'pointer',
            padding: 4,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="Adiar atualização"
        >
          <X size={16} />
        </button>
      </div>

      {/* Botões de Ação */}
      <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
        <button
          type="button"
          onClick={handleApplyUpdate}
          disabled={isUpdating}
          style={{
            flex: 1,
            padding: '9px 14px',
            borderRadius: 10,
            border: 'none',
            background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
            color: '#ffffff',
            fontSize: '0.8rem',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.35)',
            transition: 'all 0.15s ease',
          }}
        >
          <RefreshCw size={14} style={{ animation: isUpdating ? 'pwa-spin 1s linear infinite' : 'none' }} />
          {isUpdating ? 'Atualizando...' : 'Atualizar Agora'}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          style={{
            padding: '9px 12px',
            borderRadius: 10,
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(255, 255, 255, 0.06)',
            color: '#94a3b8',
            fontSize: '0.78rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Mais tarde
        </button>
      </div>

      <style>{`
        @keyframes pwa-slide-down {
          from { opacity: 0; transform: translate(-50%, -20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        @keyframes pwa-pulse {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 2px rgba(129, 140, 248, 0.4)); }
          50% { transform: scale(1.1); filter: drop-shadow(0 0 8px rgba(129, 140, 248, 0.8)); }
        }
        @keyframes pwa-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
